import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

interface MessageRule {
  id: string;
  name: string;
  rule_key: string;
  tier: number;
  template_key?: string;
  trigger_conditions: any;
  timing_config: any;
  is_active: boolean;
  priority_order: number;
  created_at: string;
  updated_at: string;
}

interface RuleEditorProps {
  rule: MessageRule | null;
  templates: MessageTemplate[];
  onSave: (rule: Partial<MessageRule>) => void;
  onCancel: () => void;
}

interface MessageTemplate {
  id: string;
  name: string;
  template_key: string;
  category: string;
  is_active: boolean;
}

const RuleEditor = ({ rule, templates, onSave, onCancel }: RuleEditorProps) => {
  const availableActionTypes = [
    'follow_up_call', 'appointment_booked', 'hand_raised', 'callback_requested', 
    'interested', 'not_interested', 'voicemail_left', 'no_answer', 'busy_signal', 
    'invalid_number', 'message_taking', 'email_provided', 'consent_callback', 
    'appointment_interest'
  ];

  const defaultTriggerConditions = {
    types: ['follow_up_call', 'appointment_booked', 'hand_raised', 'callback_requested', 'interested', 'not_interested', 'voicemail_left', 'no_answer', 'busy_signal', 'invalid_number', 'message_taking']
  };

  const defaultTimingConfig = {
    delay_minutes: 0
  };

  const [formData, setFormData] = useState({
    name: rule?.name || '',
    rule_key: rule?.rule_key || '',
    tier: rule?.tier || 1,
    template_key: rule?.template_key || '',
    trigger_conditions: rule?.trigger_conditions || defaultTriggerConditions,
    timing_config: rule?.timing_config || defaultTimingConfig,
    is_active: rule?.is_active ?? true,
    priority_order: rule?.priority_order || 0
  });

  const [showAdvancedJson, setShowAdvancedJson] = useState(false);
  const [jsonError, setJsonError] = useState('');

  // Get selected action types from trigger conditions
  const selectedTypes = formData.trigger_conditions?.types || formData.trigger_conditions?.action_types || [];

  const handleTypeToggle = (type: string) => {
    const currentTypes = [...selectedTypes];
    const index = currentTypes.indexOf(type);
    
    if (index > -1) {
      currentTypes.splice(index, 1);
    } else {
      currentTypes.push(type);
    }

    setFormData(prev => ({
      ...prev,
      trigger_conditions: {
        ...prev.trigger_conditions,
        types: currentTypes
      }
    }));
  };

  const handleJsonChange = (field: 'trigger_conditions' | 'timing_config', value: string) => {
    try {
      const parsed = JSON.parse(value);
      setFormData(prev => ({ ...prev, [field]: parsed }));
      setJsonError('');
    } catch (error) {
      setJsonError(`Invalid JSON in ${field}: ${error.message}`);
    }
  };

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="rule-name">Name</Label>
          <Input
            id="rule-name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Rule name"
          />
        </div>
        <div>
          <Label htmlFor="rule-key">Rule Key</Label>
          <Input
            id="rule-key"
            value={formData.rule_key}
            onChange={(e) => setFormData(prev => ({ ...prev, rule_key: e.target.value }))}
            placeholder="Rule key (e.g., immediate_basic)"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="rule-tier">Tier</Label>
          <Select value={formData.tier.toString()} onValueChange={(value) => setFormData(prev => ({ ...prev, tier: parseInt(value) }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Tier 1 (Immediate)</SelectItem>
              <SelectItem value="2">Tier 2 (Digest)</SelectItem>
              <SelectItem value="3">Tier 3 (Daily)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="rule-priority">Priority Order</Label>
          <Input
            id="rule-priority"
            type="number"
            value={formData.priority_order.toString()}
            onChange={(e) => setFormData(prev => ({ ...prev, priority_order: parseInt(e.target.value) || 0 }))}
            placeholder="0"
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="template-key">Email Template</Label>
        <Select value={formData.template_key || "default"} onValueChange={(value) => setFormData(prev => ({ 
          ...prev, 
          template_key: value === "default" ? "" : value 
        }))}>
          <SelectTrigger>
            <SelectValue placeholder="Select template (optional - will use tier default if not set)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Use Tier Default</SelectItem>
            {templates.filter(t => t.is_active).map((template) => (
              <SelectItem key={template.id} value={template.template_key}>
                {template.name} ({template.template_key})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground mt-1">
          Override the default tier-based template with a specific template
        </p>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Action Types</Label>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowAdvancedJson(!showAdvancedJson)}
          >
            {showAdvancedJson ? 'Simple Mode' : 'Advanced JSON'}
          </Button>
        </div>
        
        {!showAdvancedJson ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Select which action types trigger this notification rule:
            </p>
            <div className="flex flex-wrap gap-2">
              {availableActionTypes.map((type) => (
                <Badge
                  key={type}
                  variant={selectedTypes.includes(type) ? "default" : "outline"}
                  className="cursor-pointer hover:bg-primary/20"
                  onClick={() => handleTypeToggle(type)}
                >
                  {type.replace('_', ' ')}
                  {selectedTypes.includes(type) && (
                    <X className="ml-1 h-3 w-3" />
                  )}
                </Badge>
              ))}
            </div>
            <div className="text-xs text-muted-foreground">
              Selected: {selectedTypes.length === 0 ? 'None (will match all types)' : selectedTypes.join(', ')}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Advanced JSON configuration for trigger conditions:
            </p>
            <Textarea
              value={JSON.stringify(formData.trigger_conditions, null, 2)}
              onChange={(e) => handleJsonChange('trigger_conditions', e.target.value)}
              placeholder='{"types": ["follow_up_call", "message_taking"]}'
              rows={6}
              className={jsonError ? 'border-red-500' : ''}
            />
            {jsonError && (
              <p className="text-xs text-red-500">{jsonError}</p>
            )}
          </div>
        )}
      </div>
      
      <div>
        <Label>Timing Config (JSON)</Label>
        <p className="text-sm text-muted-foreground mb-2">
          Set delay_minutes: 0 for immediate processing, or higher for delayed sending.
        </p>
        <Textarea
          value={JSON.stringify(formData.timing_config, null, 2)}
          onChange={(e) => handleJsonChange('timing_config', e.target.value)}
          placeholder='{"delay_minutes": 0}'
          rows={4}
          className={jsonError ? 'border-red-500' : ''}
        />
      </div>
      
      <div className="flex items-center space-x-2">
        <Switch
          id="rule-active"
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
        />
        <Label htmlFor="rule-active">Active</Label>
      </div>
      
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave}>Save Rule</Button>
      </DialogFooter>
    </div>
  );
};

export default RuleEditor;